import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import { ReactNode, useContext, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Inventory2 from '@mui/icons-material/Inventory2';
import LinkOff from '@mui/icons-material/LinkOff';
import MoneyOff from '@mui/icons-material/MoneyOff';
import Route from '@mui/icons-material/Route';
import { SearchLg } from '../../../../assets';
import { Section } from '../../../../components/ui/Section';
import { UserContext } from '../../../../components/shared/useUser';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { TableColumn, TableSectionProps, TableSortDirection } from '../../../../components/shared/tables/TableSection';
import { InsightTile } from '../../../../components/shared/tiles/InsightTile';
import type { ListPageHeadline } from '../../../../components/page-templates/list-page/ListPageTemplate';
import { MarketItem, MarketItemFilterOptions, client } from '../../../../app/services/api/orderManagementClient';
import useLogError from '../../../../hooks/useLogError';
import { useInstanceMarket } from '../../../../contexts/InstanceMarketContext';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type FilterChangeCallback = (
  instance: string,
  market: string,
  searchTerm?: string,
  sortColumn?: string,
  sortDirection?: 'asc' | 'desc',
  page?: number,
) => void;

type Props = {
  onCreateNew: () => void;
  onFilterChange?: FilterChangeCallback;
  initialInstance?: string;
  initialMarket?: string;
  initialSearchTerm?: string;
  initialSortColumn?: string;
  initialSortDirection?: 'asc' | 'desc';
  initialPage?: number;
  /** Splits filters, tiles and table so the caller can slot them into ListPageTemplate. */
  children: (parts: {
    filterBar: ReactNode;
    headlineContent: ListPageHeadline;
    table: TableSectionProps<MarketItem>;
    snackbar: ReactNode;
    isInitialLoading: boolean;
  }) => ReactNode;
}

const ProductsListPageContent = ({
  onCreateNew,
  onFilterChange,
  initialInstance,
  initialMarket,
  initialSearchTerm,
  initialSortColumn,
  initialSortDirection,
  initialPage,
  children,
}: Props) => {
  const { t } = useTranslation('productManagement');
  const { logError } = useLogError();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermissionTo } = useContext(UserContext);
  const canEdit = hasPermissionTo(['ConfigurationAdmin']) || hasPermissionTo(['ProductUpdate']);


  const [products, setProducts] = useState<MarketItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const { instances, getMarkets, ensureMarketsLoaded, defaultFiltersLoading } = useInstanceMarket();
  const markets = getMarkets(selectedInstance);
  const [sortColumn, setSortColumn] = useState<string>('itemNumber');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Snackbar states
  const [showSubmitStatusMessage, setShowSubmitStatusMessage] = useState<boolean>(false);
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const pageSize = 20;

  // Headline tiles summarise the whole Instance/Market catalogue (not the current search/page), fetched once per scope change so they don't slow down search/sort/paging.
  const [statsList, setStatsList] = useState<MarketItem[]>([]);
  const [isStatsLoading, setIsStatsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!selectedInstance || !selectedMarket) {
      setStatsList([]);
      return;
    }

    setIsStatsLoading(true);
    client.getMarketItems({
      companyId: selectedInstance,
      customerTypeId: selectedMarket,
      pageNumber: 0,
      pageSize: 2147483647,
    })
      .then((result) => {
        if (result.success && result.data) {
          setStatsList(result.data.items ?? []);
        }
      })
      .catch((error) => console.error('Error fetching product stats:', error))
      .finally(() => setIsStatsLoading(false));
  }, [selectedInstance, selectedMarket]);

  const hasOrderablePrice = (item: MarketItem) => {
    const uom = item.uoms?.find((u) => u.isOrderableUom) ?? item.uoms?.find((u) => u.isPrimaryUom);
    return uom?.price != null;
  };

  const notAssignedToCatalogueCount = useMemo(() => (
    statsList.filter((item) => (item.catalogueIds ?? []).length === 0).length
  ), [statsList]);

  const missingPriceCount = useMemo(() => (
    statsList.filter((item) => !hasOrderablePrice(item)).length
  ), [statsList]);

  const notAssignedToOrderChannelCount = useMemo(() => (
    statsList.filter((item) => (item.orderTypeIds ?? []).length === 0).length
  ), [statsList]);

  useEffect(() => {
    if (initialInstance) setSelectedInstance(initialInstance);
    if (initialMarket) setSelectedMarket(initialMarket);
    if (initialSearchTerm !== undefined) {
      setSearchInput(initialSearchTerm);
      setSearchTerm(initialSearchTerm);
    }
    if (initialSortColumn) setSortColumn(initialSortColumn);
    if (initialSortDirection) setSortDirection(initialSortDirection);
    if (initialPage !== undefined) setCurrentPage(initialPage);
  }, [initialInstance, initialMarket, initialSearchTerm, initialSortColumn, initialSortDirection, initialPage]);

  useEffect(() => {
    if (!selectedInstance) return;
    ensureMarketsLoaded(selectedInstance);
  }, [selectedInstance, ensureMarketsLoaded]);

  useEffect(() => {
    if (selectedInstance && selectedMarket) {
      loadProducts();
    } else {
      setProducts([]);
      setTotalPages(0);
      setTotalItems(0);
    }
  }, [currentPage, searchTerm, selectedInstance, selectedMarket, sortColumn, sortDirection]);

  useEffect(() => {
    if (markets.length === 1) {
      const onlyMarket = markets[0].customerTypeId.toString();
      setSelectedMarket(onlyMarket);
      onFilterChange?.(selectedInstance, onlyMarket, searchTerm);
    }
  }, [markets]);

  const loadProducts = async () => {
    if (!selectedInstance || !selectedMarket) return;
    
    setIsLoading(true);
    try {
      const filterOptions: MarketItemFilterOptions = {
        companyId: selectedInstance,
        customerTypeId: selectedMarket,
        searchTerm: searchTerm || undefined,
        sort: sortColumn,
        direction: sortDirection,
        pageNumber: currentPage,
        pageSize: pageSize,
      };

      const response = await client.getMarketItems(filterOptions);
      
      if (response.success && response.data) {
        setProducts(response.data.items || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalItems(response.data.totalCount || 0);
      } else {
        setErrorMessage(response.message || t('loadErrorMessage'));
        setIsSubmittedSuccessfully(false);
        setShowSubmitStatusMessage(true);
      }
    } catch (error) {
      logError('Failed to load products');
      setErrorMessage(t('loadErrorMessage'));
      setIsSubmittedSuccessfully(false);
      setShowSubmitStatusMessage(true);
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSortChange = (column: string, direction: TableSortDirection) => {
    setSortDirection(direction);
    setSortColumn(column);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, selectedMarket, searchTerm, column, direction, 0);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    onFilterChange?.(selectedInstance, selectedMarket, searchTerm, sortColumn, sortDirection, page);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, selectedMarket, searchInput, sortColumn, sortDirection, 0);
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    const newInstance = event.target.value;
    setSelectedInstance(newInstance);
    setSelectedMarket('');
    setCurrentPage(0);
    onFilterChange?.(newInstance, '', searchTerm, sortColumn, sortDirection, 0);
  };

  const handleMarketChange = (event: SelectChangeEvent<string>) => {
    const newMarket = event.target.value;
    setSelectedMarket(newMarket);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, newMarket, searchTerm, sortColumn, sortDirection, 0);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedInstance('');
    setSelectedMarket('');
    setCurrentPage(0);
    onFilterChange?.('', '', '', sortColumn, sortDirection, 0);
  };

  const onClear = () => {
    handleClear();
  };

  const handleCloseMessage = () => {
    setShowSubmitStatusMessage(false);
  };

  const maxPages = () => {
    return Math.max(1, totalPages);
  };

  const showSelectMessage = !selectedInstance || !selectedMarket;

  const filterBar = (
    <Grid container
      spacing={5}>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <TextField
          label={t('search')}
          placeholder={t('searchPlaceholder')}
          value={searchInput}
          onChange={(event) => handleSearchInputChange(event.target.value)}
          onKeyPress={handleSearchKeyPress}
          fullWidth
          disabled={showSelectMessage}
        />
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={handleClear}
          variant='secondary'
          size='lg'
          disabled={showSelectMessage}
        >
          {t('clear')}
        </Button>
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={handleSearch}
          size='lg'
          startIcon={<SvgIcon><SearchLg /></SvgIcon>}
          disabled={showSelectMessage}
        >
          {t('search')}
        </Button>
      </Grid>
      <Grid item
        lg={2.5}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('filterByInstance')}
          </InputLabel>
          <Select
            value={instances.some(m => m.id?.toString() == selectedInstance) ? selectedInstance : ''}
            onChange={handleInstanceChange}
            label={t('filterByInstance')}
            displayEmpty
            notched
          >
            <MenuItem value="">
              <em>{t('selectInstance')}</em>
            </MenuItem>
            {instances.map((instance) => (
              <MenuItem key={instance.id}
                value={instance.id.toString()}>
                {instance.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        lg={2.5}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('filterByMarket')}
          </InputLabel>
          <Select
            value={markets.some(m => m.customerTypeId?.toString() == selectedMarket) ? selectedMarket : ''}
            onChange={handleMarketChange}
            label={t('filterByMarket')}
            displayEmpty
            notched
            disabled={!selectedInstance}
          >
            <MenuItem value="">
              <em>{t('selectMarket')}</em>
            </MenuItem>
            {markets.map((market) => (
              <MenuItem key={market.customerTypeId}
                value={market.customerTypeId.toString()}>
                {market.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const handleRowClick = (item: MarketItem) => {
    if (!canEdit) return;
    const params = new URLSearchParams();
    params.set('companyId', item.companyId.toString());
    if (item.customerTypeId !== undefined) {
      params.set('customerTypeId', item.customerTypeId.toString());
    }
    ['searchTerm', 'sort', 'direction', 'page'].forEach((key) => {
      const value = searchParams.get(key);
      if (value) params.set(key, value);
    });
    navigate(`/procurement/products/edit/${item.contentId}?${params.toString()}`);
  };

  const columns: TableColumn<MarketItem>[] = [
    { id: 'itemNumber', label: t('itemNumber'), field: 'itemNumber', sortable: true, sortKey: 'itemNumber' },
    { id: 'itemName', label: t('itemName'), field: 'itemName', sortable: true, sortKey: 'itemName' },
    { id: 'inventorySKU', label: t('inventorySKU'), render: (row) => row.marketItemNumber || '-' },
    { id: 'category', label: t('category'), render: (row) => row.primaryCategoryName || row.marketPrimaryCategoryName || '-' },
    { id: 'primaryUom', label: t('primaryUom'), field: 'primaryUom' },
    { id: 'orderableUom', label: t('orderableUom'), field: 'orderableUom' },
    {
      id: 'orderableUomPrice',
      label: t('orderableUomPrice'),
      render: (row) => {
        const orderableUomPrice = row.uoms?.find((uom) => uom.isOrderableUom)?.price;
        return orderableUomPrice != null ? orderableUomPrice : '-';
      },
    },
    { id: 'packSize', label: t('packSize'), render: (row) => row.packSize || '-' },
    { id: 'caseSize', label: t('caseSize'), render: (row) => row.caseSize || '-' },
    { id: 'innerPackSize', label: t('innerPackSize'), render: (row) => row.innerPackSize || '-' },
  ];

  const table: TableSectionProps<MarketItem> = {
    title: t('products'),
    columns,
    rows: products,
    getRowId: (row) => `${row.companyId}-${row.customerTypeId}-${row.itemNumber}`,
    onRowClick: canEdit ? handleRowClick : undefined,
    recordCount: totalItems,
    recordCountLabel: t('products'),
    loading: isLoading,
    emptyContent: showSelectMessage ? (
      <Section>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '700px',
            textAlign: 'center',
          }}
        >
          <Typography variant="textMD"
            color="text.secondary"
            gutterBottom>
            {t('selectInstanceAndMarket')}
          </Typography>
        </Box>
      </Section>
    ) : (
      <EmptyTable
        bodyRowHeight={40}
        headerHeight={45}
        text={t('products')}
        searchTerm=""
        onClear={onClear}
        onAdd={onCreateNew}
        onAddIsCreating={false}
        addItemText={t('createNew')}
        onAddIsDisabled={false}
        withPaginationShown={false}
      />
    ),
    page: currentPage,
    maxPages: maxPages(),
    onPageChange: handlePageChange,
    sortBy: sortColumn,
    sortDirection,
    onSortChange: handleSortChange,
  };

  const snackbar = (
    <Snackbar
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      open={showSubmitStatusMessage}
      onClose={handleCloseMessage}
      autoHideDuration={5000}
    >
      <Alert
        onClose={handleCloseMessage}
        severity={isSubmittedSuccessfully ? 'success' : 'error'}
        variant="filled"
        sx={{ width: '100%' }}
      >
        {isSubmittedSuccessfully ? (errorMessage || t('successMessage')) : errorMessage}
      </Alert>
    </Snackbar>
  );

  const headlineContent: ListPageHeadline = {
    insights: [
      <InsightTile
        key="totalProducts"
        label={t('tileTotalProducts', 'Total Products')}
        value={statsList.length}
        description={t('tileTotalProductsDescription', 'All products in the selected Instance/Market')}
        icon={<Inventory2 />}
        variant="primary"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="notAssignedToCatalogue"
        label={t('tileNotAssignedToCatalogue', 'Not Assigned to Any Catalogue')}
        value={notAssignedToCatalogueCount}
        description={t('tileNotAssignedToCatalogueDescription', 'Products not in any catalogue')}
        icon={<LinkOff />}
        variant="warning"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="missingPrice"
        label={t('tileMissingPrice', 'Missing Price')}
        value={missingPriceCount}
        description={t('tileMissingPriceDescription', 'Products with no price on their orderable unit')}
        icon={<MoneyOff />}
        variant="error"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="notAssignedToOrderChannel"
        label={t('tileNotAssignedToOrderChannel', 'Not Assigned to Any Order Channel')}
        value={notAssignedToOrderChannelCount}
        description={t('tileNotAssignedToOrderChannelDescription', 'Products with no order channel')}
        icon={<Route />}
        variant="secondary"
        loading={isStatsLoading}
      />,
    ],
  };
  return <>{children({
    filterBar,
    headlineContent,
    table,
    snackbar,
    // Still resolving the default instance/market before the first fetch even starts - keep showing the skeleton, not a zero-data flash.
    isInitialLoading: (defaultFiltersLoading || isLoading) && !hasLoadedOnce,
  })}</>;
};

export default ProductsListPageContent;
